import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import gql from 'graphql-tag';
import React from 'react';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { CreditCard, Event, Payline, PaymentProvider, helpers } from '@aps-management/primapp-common';

import Payment from '_components/Payment';
import { Screen } from '_components/core';
import { DialogPaymentIP, PaylineWidgetFrame } from '_components/elements';
import apolloClient from '_utils/apolloClient';
import { buildEntryDeliveryIntent } from '_utils/deliveryIntent';
import i18n from '_utils/i18n';
import masterClient from '_utils/masterClient';
import { captureWithTokenWithDelivery, checkPaymentWithDelivery } from '_utils/paylineWidgetDelivery';
import { assertCaptured, assertWidgetPaymentSuccess, buildTransactionFields } from '_utils/paymentGuard';

/* */
import { PAYLINE_FLOW_TYPES, setupPaylineListeners } from '../../../services/payline';

/* */
const styles = {};

/*
 * Vérifie ET persiste côté serveur le paiement encaissé par le widget hébergé.
 *
 * `checkPayment` ne persiste RIEN : entre le succès du widget et `setEntry`, le débit
 * n'existe nulle part côté PrimApp. Si la réponse se perd, il devient invisible, donc
 * ni récupérable ni remboursable (incident LAZZARETTO, et 2 orphelins constatés sur ce
 * tunnel les 27-28/07 : 10 € et 7 €).
 *
 * Le Booking passe `orderId` parce qu'il crée sa commande AVANT de payer. Ici la
 * commande naît dans `setEntry` : le paiement est donc porté par `reference`, et
 * `SalesOrder.confirm` le rattachera ensuite par `transaction_no`.
 */
const RECORD_WIDGET_PAYMENT = gql`
  mutation PaylineRecordWidgetPayment($reference: String, $amount: Int!, $token: String!) {
    paylineRecordWidgetPayment(reference: $reference, amount: $amount, token: $token) {
      transactionId
      authorizationNumber
      status
      code
      maskedCardNumber
      cardBrand
      cardCountry
      # Ce que master a livre de lui-meme au retour du widget. Ce document part sur
      # masterClient et non sur le proxy, contrairement aux deux autres retours de
      # widget qui vivent dans _utils/paylineWidgetDelivery : il ne depend donc
      # que du schema de master.
      #
      # Pas d'accent grave dans ce bloc : il vit dans un litteral de gabarit
      # JavaScript, qu'un accent grave fermerait au milieu du document.
      delivery {
        done
        status
        scope
      }
    }
  }
`;

/*
 * Capture en un clic (carte enregistrée), avec l'intention de livraison.
 *
 * Pourquoi un document déclaré ici plutôt que `Payline.api.captureSubscriber` : celui
 * de `@aps-management/primapp-common` ne déclare pas `$deliveryIntent`, et une variable
 * absente du document n'atteint JAMAIS le résolveur - elle serait ignorée en silence.
 * Même contournement que `RECORD_WIDGET_PAYMENT` ci-dessus.
 *
 * Ce document est une copie de `constants.gql.CAPTURE_SUBSCRIBER` : la sélection de
 * champs y est identique, à l'ajout de `deliveryIntent` près. Il passe par le proxy
 * `primapp-api`, qui déclare et relaie ce champ vers master.
 */
const CAPTURE_SUBSCRIBER_WITH_INTENT = gql`
  mutation PaylineCaptureSubscriber(
    $amount: Int!
    $reference: String!
    $subscriber: PaylineSubscriber!
    $deliveryIntent: String
  ) {
    paylineCaptureSubscriber(
      amount: $amount
      reference: $reference
      subscriber: $subscriber
      deliveryIntent: $deliveryIntent
    ) {
      transactionId
      token
      authorizationNumber
      resultCode
      needs3DS
      # Ce que master a livre de lui-meme dans la foulee de la capture. Nul tant
      # que EVENT_setEntry reste hors de sa liste blanche, et nul sur tout serveur
      # anterieur au lot du 14/08 : un front qui ne selectionne pas ce champ le
      # recoit nul et se comporte comme avant.
      #
      # Pas d'accent grave dans ce bloc : il vit dans un litteral de gabarit
      # JavaScript, qu'un accent grave fermerait au milieu du document.
      delivery {
        done
        status
        scope
      }
      threeDSData {
        actionUrl
        actionMethod
        pareq
        pareqFieldName
        pareqFieldValue
        md
        mdFieldName
        mdFieldValue
        termUrl
        mpiResult
        transientData
      }
    }
  }
`;

/*
 * Reprise à l'identique de `Payline.api.captureSubscriber` : `needs3DS` est rendu tel
 * quel (un challenge 3DS n'est pas une erreur, l'appelant le traite), le reste passe
 * par `isPaylineSuccess` puis `handlePaylineError`, dans le même ordre et avec le même
 * enchaînement `then`/`catch`. Seule la transmission de l'intention change.
 */
const captureSubscriberWithIntent = (variables) =>
  apolloClient
    .mutate({
      mutation: CAPTURE_SUBSCRIBER_WITH_INTENT,
      variables,
    })
    .then(({ data }) => {
      const response = data ? data.paylineCaptureSubscriber : undefined;
      if (response && response.needs3DS) {
        return response;
      }
      if (!Payline.errors.isPaylineSuccess(response)) {
        throw Payline.errors.handlePaylineError(null, response);
      }
      return response;
    })
    .catch((error) => {
      throw Payline.errors.handlePaylineError(error);
    });

/* */

/* */
class TournamentPayment extends React.Component {
  /* */
  constructor(props) {
    super(props);

    const {
      golf,
      enter,
      account,
      match: { params },
    } = props;

    this.id = params.id;
    this.redirect = false;

    if (!enter.status.includes('cart')) {
      this.redirect = true;
      return;
    }

    // check all required options are selected
    const requiredEvents = enter.events.filter((ev) => ev && ev.required);
    const checkEnterEvents =
      requiredEvents.length > 0
        ? enter.entrants.every((en) => requiredEvents.every((req) => en.events.find((ev) => ev && ev.id === req.id)))
        : true;

    // init data
    const {
      total,
      events,
      // entrants,
      tournament,
    } = enter;

    this.data = {
      amount: total.tournament + total.events,
      // entrants.reduce((tot, entrant) => (tot
      //   + ((entrant.tournament
      //       && (entrant.tournament.greenFee + entrant.tournament.entryFee)) || 0)
      //   + ((entrant.events
      //       && entrant.events.reduce((acc, e) => (acc + e.amount), 0)) || 0)
      // ), 0),
      reference: (() => {
        const shortTournamentId = tournament ? tournament.id.split('-')[0] : '';
        const shortEventId = events.length ? events[0].id.split('-')[0] : '';
        return shortTournamentId && shortEventId
          ? `TOUR-${shortTournamentId}-EVT-${shortEventId}`
          : shortTournamentId
            ? `TOUR-${shortTournamentId}`
            : shortEventId
              ? `EVT-${shortEventId}`
              : '';
      })(),
      walletUuid: helpers.uuid.createUuid(),
    };

    // build session 3DS
    this.session3DS = {
      address: {
        city: golf.city,
        countryCode: 250, // golf.country
        line1: golf.name,
        zipCode: golf.postalCode,
      },
      customer: {
        email: account.email,
        lastName: account.lastname,
        firstName: account.firstname,
        phone: account.phone,
      },
    };
    this.currency = golf.currency;

    this.state = {
      error: null,
      status: null,
      imprint: null,
      loading: true,
      waiting: false,
      expanded: null,
      checkEnterEvents,
      paylineToken: null,
    };

    this.paylineWidgetData = null;
    this.isPaylinePaymentHandled = false;
    this.removePaylineListeners = null;
  }

  /* */
  componentDidMount() {
    const { account } = this.props;
    if (account?.id) {
      CreditCard.api
        .getCreditCards(apolloClient, { accountId: account.id })
        .then(({ creditCards }) => {
          // Garde-fou : seule une carte disposant d'un wallet Payline réel (walletId) peut
          // être débitée en 1 clic. Une carte sans walletId (enregistrée avant le correctif
          // ou via un chemin legacy) produirait subscriber.id=null : mutation rejetée. On ne
          // la propose donc pas : l'utilisateur ressaisit sa carte (le widget crée un vrai
          // wallet), ce qui répare la situation de lui-même.
          const usable = (creditCards || []).find((c) => c && c.walletId);
          if (usable) {
            this.setState({ imprint: usable });
          }
        })
        .catch(() => {})
        .finally(() => {
          this.setState({ loading: false });
        });
    } else {
      this.setState({ loading: false });
    }
  }

  /*
   * `setupPaylineListeners` pose ses écouteurs sur `window`, pas sur le composant :
   * sans cette libération, ils survivent au démontage de l'écran. Un aller-retour
   * (retour au panier puis nouvelle tentative) laisse alors deux instances à
   * l'écoute du même widget, et le succès du paiement déclenche DEUX fois la
   * chaîne de confirmation - chacune avec son propre garde-fou d'instance, qui ne
   * voit donc rien. Cf. incident Golf aux Daims du 25/07/2026 (rechargement de
   * compte client encaissé une fois, crédité deux fois).
   */
  componentWillUnmount() {
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }
  }

  /* */
  /*
   * Ce qui suit une inscription acquise, qu'elle vienne du serveur ou de cet
   * ecran. Extrait pour que les deux chemins ne puissent pas diverger.
   */
  concludeEntry = (transaction) => {
    // L'inscription est acquise : plus rien en aval ne doit annoncer un echec ni
    // faire annuler le paiement.
    this.entryConfirmed = true;
    this.props.enter.transaction = transaction;
    this.goToVoucher();
  };

  /**
   * @param {boolean} [dejaLivre] Le serveur a deja inscrit dans la foulee de la
   *   capture, cet ecran ne doit pas rappeler `asyncSetEntry`.
   */
  setEntry = (dejaLivre = false) => {
    const { enter, golf } = this.props;
    const { transaction } = this.data;

    /*
     * LE SERVEUR A DEJA INSCRIT : ne pas rappeler.
     *
     * Inatteignable aujourd'hui - `EVENT_setEntry` n'est pas dans
     * `PERIMETRES_LIVRABLES_IMMEDIATEMENT` cote master, donc `done` y est toujours
     * faux. La garde est posee MAINTENANT pour que l'ouverture de ce perimetre
     * n'ait plus a attendre les fronts. C'est l'avertissement qu'`immediateDelivery.js`
     * adresse explicitement aux fronts.
     *
     * Sans elle, une seconde action 71 partirait au PGB pour un seul debit, et le
     * golfeur serait inscrit DEUX FOIS chez le golf, irreversiblement.
     */
    if (dejaLivre) {
      return Promise.resolve(this.concludeEntry(transaction));
    }

    return Event.functions.asyncSetEntry(apolloClient, { enter, golf, transaction }).then((result) => {
      // TODO: update contacts with new tid
      if (result && result.players) {
        //   players.forEach(({ tid }, index) => {
        //     if (entrants[index].reference !== tid) {
        //     }
        //   });
      }
      this.concludeEntry(transaction);
    });
  };

  /* */
  goToVoucher = () => {
    this.props.enter.status.push('payment');
    this.props.history.push(`/tournament/${this.id}/voucher`);
  };

  /* */
  payWithCardImprint = () => {
    /*
     * Chaque tentative repart d'une transaction vierge. Sans cette remise à zéro,
     * une seconde tentative qui échoue AVANT sa propre capture (refus banque,
     * coupure réseau, verrou anti-doublon) relit le `transactionNo` de la première
     * et fait afficher « Votre paiement a bien été accepté [...] contactez le golf
     * en indiquant la référence X » alors que rien n'a été encaissé cette fois-ci.
     * Le garde `!err.notCaptured` ne couvre que le 3DS et la réponse vide.
     * Même correction que sur primapp-native. Le chemin widget, lui, remet déjà
     * la valeur à null dans son propre `catch`.
     */
    this.data.transaction = null;
    /*
     * Et le drapeau d'inscription avec elle. Sans ce reset, une tentative
     * anterieure conclue laissait `entryConfirmed` a vrai pour toujours, et la
     * garde de `handlePostCaptureFailure` avalait ensuite jusqu'au refus banque
     * franc. Le natif le remet a zero au meme endroit, dans `beginAttempt`.
     */
    this.entryConfirmed = false;
    const { imprint } = this.state;

    // Garde-fou (défense en profondeur) : ne jamais tenter un paiement 1-clic avec une
    // carte dépourvue de wallet Payline réel. Normalement déjà filtré au chargement.
    if (!imprint || !imprint.walletId) {
      this.setState({
        error:
          "Cette carte enregistrée n'est plus utilisable pour le paiement en 1 clic. Réglez avec une nouvelle carte : elle sera ré-enregistrée automatiquement.",
      });
      return Promise.resolve();
    }

    const subscriber = {
      id: imprint.walletId,
      token: imprint.token,
      dueDate: imprint.dueDate,
    };

    this.setState({
      error: null,
      waiting: true,
      status: 'Nous interrogeons votre banque...',
    });

    return captureSubscriberWithIntent({
      ...this.data,
      subscriber,
      // Décrit l'inscription achetée pour que master puisse la reprendre si `setEntry`
      // n'aboutit jamais. Absente ou nulle, le comportement est exactement celui d'avant.
      deliveryIntent: buildEntryDeliveryIntent({ enter: this.props.enter, golf: this.props.golf }),
    })
      .catch((err) => {
        // Échec de la capture : aucun débit n'a eu lieu, il n'y a donc rien à
        // annuler. Le message d'attente ne doit pas promettre une annulation.
        if (!err.handled) {
          this.setState({
            status: 'Paiement refusé par votre banque...',
          });
        }
        throw Object.assign(err, { handled: true });
      })
      .then((res) => {
        // Sur challenge 3DS (01131), le serveur répond `{ needs3DS: true }` SANS
        // transactionId : rien n'a été encaissé. Sans cette garde, l'inscription
        // était livrée gratuitement sur toute carte soumise à SCA.
        const captured = assertCaptured(res, 'Tournament.captureSubscriber');

        this.data.transaction = {
          mode: 0, // par CB
          card: imprint.number,
          amount: this.data.amount,
          creditCardId: imprint.id,
          ...buildTransactionFields(captured),
          ...helpers.object.filterByKeys(imprint, ['type', 'brand', 'country']),
        };

        /*
         * `=== true` et non une valeur vraie quelconque : ce champ est nul sur tout
         * serveur anterieur au lot du 14/08. Une lecture laxiste ferait sauter
         * l'inscription devant un serveur qui n'a rien livre.
         *
         * `res` et `captured` sont le MEME objet, `assertCaptured` rendant son
         * argument. On lit `res` pour rester aligne sur le natif et l'embed.
         */
        return this.setEntry(res?.delivery?.done === true);
      })
      .catch(this.handlePostCaptureFailure);
  };

  /*
   * Échec de l'inscription APRÈS un encaissement réussi.
   *
   * Le porteur est débité, la prestation n'est pas livrée, et ce tunnel ne dispose
   * d'AUCUNE reprise serveur idempotente (contrairement au Booking et à l'Activité,
   * qui ont `bookingConfirmIfCaptured`). Afficher l'erreur technique brute laissait
   * croire que le paiement n'avait pas eu lieu, et le golfeur réessayait - d'où les
   * doubles débits remontés par les golfs.
   *
   * À défaut de rattrapage automatique, on dit la vérité et on donne la référence
   * de transaction, seule donnée qui permet au golf de retrouver le paiement.
   * On ne promet PAS de notification : aucune n'est envoyée aujourd'hui.
   */
  handlePostCaptureFailure = (err) => {
    /*
     * L'INSCRIPTION EST ACQUISE : l'erreur est posterieure, elle ne dit rien du
     * paiement ni de l'inscription.
     *
     * Sans cette garde, une exception levee par la navigation ou par
     * `enter.status.push` APRES une inscription reussie tombait ici, et le golfeur
     * lisait « votre inscription n'a pas pu etre enregistree, contactez le golf »
     * sur une inscription qui tient. Le golf remboursait alors une prestation
     * livree. Le cas devient certain sur la branche `dejaLivre`, ou l'inscription
     * est un fait etabli par le serveur et non une esperance.
     *
     * Meme role que le `entryConfirmed` de primapp-native, ou la garde equivalente
     * ouvre `handleEntryFailure`.
     */
    if (this.entryConfirmed) {
      // eslint-disable-next-line no-console
      console.error('[payment] afterEntryConfirmed.error', err && err.message);
      /*
       * On rend l'ecran, mais JAMAIS muet, et c'est le meme mot d'ordre que sur
       * primapp-native : sans message, le golfeur retrouve un formulaire de
       * paiement intact apres une inscription pourtant payee et confirmee, et un
       * seul appui rejoue le debit. Ni ce depot ni l'embed n'ont l'equivalent du
       * verrou `paymentCaptured` du natif : ici, le message est la seule chose qui
       * empeche le second debit, d'ou son insistance.
       */
      this.setState({
        waiting: false,
        status: null,
        error:
          'Votre inscription est bien enregistree et votre paiement a ete accepte. '
          + 'Ne payez pas une seconde fois : retrouvez votre inscription depuis l\u2019accueil.',
      });
      return;
    }

    const transactionNo = this.data.transaction && this.data.transaction.transactionNo;

    /*
     * Depuis le 04/08/2026, master annule RÉELLEMENT le paiement quand l'inscription
     * échoue. Auparavant `isPaylineProvider` lisait `options.paymentProvider.value`
     * sur un tableau de `{ name, value }` dont la valeur est une chaîne JSON : le
     * test rendait toujours `false` et la compensation ne partait jamais. Le web
     * n'avait donc jamais vu le verdict `reversed`, et affichait à tout le monde le
     * message « contactez le golf avec la référence ».
     *
     * Ce message reste juste quand l'argent est resté pris, il devient faux et
     * inutilement inquiétant quand il a été rendu. On lit donc le verdict, comme le
     * fait déjà l'écran Primaccess. `includes` et non une égalité : Apollo préfixe
     * le message serveur par « GraphQL error: ».
     */
    const verdict = [err.message, ...(err.graphQLErrors || []).map((e) => e && e.message)]
      .filter(Boolean)
      .join(' ');

    if (verdict.includes('errors.payment.reversed')) {
      this.setState({
        error:
          'Votre inscription n’a pas pu être enregistrée par le golf et votre paiement a été annulé. '
          + 'Aucun montant ne restera débité : vous pouvez réessayer.',
        waiting: false,
        status: null,
      });
      return;
    }

    if (transactionNo && !err.notCaptured) {
      // eslint-disable-next-line no-console
      console.error('[payment] Tournament.setEntry.afterCapture', transactionNo, err.message);
      this.setState({
        error:
          'Votre paiement a bien été accepté mais votre inscription n’a pas pu être enregistrée. '
          + `Ne renouvelez pas l’opération : contactez le golf en indiquant la référence ${transactionNo}.`,
        waiting: false,
        status: null,
      });
      return;
    }

    this.setState({ error: err.message, waiting: false, status: null });
  };

  /* */
  capture(creditCard) {
    const { account } = this.props;
    const { id3D = null } = creditCard;
    const { amount, reference } = this.data;

    // Réinitialisé à chaque tentative : ne persiste que si une carte est
    // réellement créée ci-dessous, pour être reporté dans la transaction.
    this.data.newCreditCardId = null;

    if (creditCard.saveImprint) {
      const uuid = helpers.uuid.createUuid();
      this.data.newCreditCardId = uuid;

      return Payline.api
        .subscribeAndCapture(apolloClient, {
          uuid,
          id3D,
          amount,
          reference: account.id,
          creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
        })
        .catch((err) => {
          throw Object.assign(err, { handled: true });
        })
        .then((res) =>
          CreditCard.api
            .setCreditCard(apolloClient, {
              id: uuid,
              token: res.token,
              number: CreditCard.functions.anonymizeNumber(creditCard.number),
              ...helpers.object.filterByKeys(creditCard, ['name', 'dueDate', 'country', 'brand', 'type']),
            })
            .then(() => res),
        )
        .catch((err) => {
          if (!err.handled) {
            throw Object.assign(err, {
              handled: true,
              message: 'Impossible d’enregistrer la carte. Veuillez réessayer ultérieurement.',
            });
          }
          throw err;
        });
    }

    return Payline.api.capture(apolloClient, {
      id3D,
      amount,
      reference,
      creditCard: helpers.object.filterByKeys(creditCard, ['number', 'dueDate', 'cvv']),
    });
  }

  /* */
  payWithNewCard = (values, { setSubmitting }) => {
    const creditCard = {
      ...values,
      name: '3DS web',
      number: values.number.replace(/\s/g, ''),
      dueDate: values.dueDate.replace(/\D/g, ''),
    };

    // Meme raison que dans `payWithCardImprint` : une tentative anterieure conclue
    // laisserait la garde de `handlePostCaptureFailure` avaler celle-ci.
    this.entryConfirmed = false;

    this.setState({
      error: null,
      waiting: true,
      status: 'Nous interrogeons votre banque...',
    });

    return this.capture(creditCard)
      .catch((err) => {
        if (!err.handled) {
          this.setState({
            status: 'Paiement refusé par votre banque...',
          });
        }
        throw err;
      })
      .then((res) => {
        const captured = assertCaptured(res, 'Tournament.captureNewCard');

        this.data.transaction = {
          mode: 0, // par CB
          amount: this.data.amount,
          card: CreditCard.functions.anonymizeNumber(creditCard.number),
          // Rattache le paiement à la carte enregistrée à la volée (null si la
          // carte n'a pas été enregistrée), sinon credit_card_id resterait NULL.
          creditCardId: this.data.newCreditCardId || null,
          ...buildTransactionFields(captured),
          ...helpers.object.filterByKeys(creditCard, ['type', 'brand', 'country']),
        };

        /*
         * AUCUN verdict de livraison sur ce chemin, et ce n'est pas un oubli.
         *
         * `capture()` passe par `Payline.api.subscribeAndCapture` ou
         * `Payline.api.capture` de `primapp-common`, dont les documents ne
         * selectionnent pas `delivery` - et cote master, ni `paylineSubscribeAndCapture`
         * ni `paylineCapture` ne declenchent la moindre livraison. Y lire
         * `res.delivery.done` rendrait `undefined` pour toujours, en donnant
         * l'illusion d'une garde.
         *
         * L'ecran livre donc lui-meme, comme avant. Les deux chemins qui portent un
         * verdict sont le paiement en un clic et le retour du widget.
         */
        return this.setEntry();
      })
      .catch((err) => {
        this.handlePostCaptureFailure(err);
        if (setSubmitting) setSubmitting(false);
      });
  };

  payWithNewCardPayline = () => {
    /*
     * Meme raison que dans `payWithCardImprint` et `payWithNewCard` : une tentative
     * ANTERIEURE conclue laisserait la garde de `handlePostCaptureFailure` avaler
     * l'echec de celle-ci, et le golfeur lirait « votre inscription est bien
     * enregistree » sur une tentative qui vient d'echouer.
     *
     * Manquait sur ce chemin-ci, alors que c'est le seul des trois a ouvrir le
     * widget. Releve par la relecture du 21/08.
     */
    this.entryConfirmed = false;

    const data = {
      amount: this.data.amount,
      reference: this.data.reference,
    };

    this.setState({
      error: null,
      waiting: true,
      status: 'Nous préparons le paiement...',
    });

    const baseUrl = window.location.origin;

    return Payline.api
      .getToken(apolloClient, {
        amount: data.amount,
        reference: data.reference,
        returnUrl: `${baseUrl}/tournament/${this.id}/voucher`,
        cancelUrl: `${baseUrl}/tournament/${this.id}/payment`,
        callbackUrl: `${baseUrl}/payline/callback`,
        customer: {
          firstName: this.props.account.firstname,
          lastName: this.props.account.lastname,
          email: this.props.account.email,
          phone: this.props.account.phone,
        },
        currency: this.currency,
        // Décrit l'inscription achetée pour que master puisse la reprendre si le
        // navigateur ne revient jamais appeler `setEntry`.
        deliveryIntent: buildEntryDeliveryIntent({ enter: this.props.enter, golf: this.props.golf }),
      })
      .then((res) => {
        if (!res?.token) {
          throw new Error('Token de paiement introuvable.');
        }

        data.token = res.token;

        this.setState({
          paylineToken: data.token,
          waiting: false,
          status: null,
        });

        this.paylineWidgetData = data;
        this.isPaylinePaymentHandled = false;

        if (this.removePaylineListeners) {
          this.removePaylineListeners();
        }

        this.removePaylineListeners = setupPaylineListeners(PAYLINE_FLOW_TYPES.CHECKOUT, {
          onSuccess: (eventData) => this.handlePaylinePaymentSuccess(eventData),
          onFailure: (eventData) =>
            this.handlePaylinePaymentError(eventData, 'Le paiement a échoué. Veuillez réessayer.'),
          onCancel: (eventData) => this.handlePaylinePaymentError(eventData, 'Le paiement a été annulé.'),
        });
      })
      .catch((err) => {
        this.setState({
          error: err.message,
          waiting: false,
          status: null,
          paylineToken: null,
        });
      });
  };

  /*
   * Remplace `checkPayment` sur le chemin "nouvelle carte sans enregistrement" : même
   * réponse, mais le paiement est en plus persisté côté serveur, porté par la référence.
   *
   * Le chemin `saveImprint` n'en a pas besoin : `paylineCaptureWithToken` appelle déjà
   * `createPendingPayment(amount, reference)` côté master.
   *
   * Strictement additif : si l'enregistrement échoue (réseau, master injoignable), on
   * retombe sur `checkPayment`. Le parcours n'est jamais plus fragile qu'avant, et le
   * golfeur n'est jamais bloqué par une écriture de traçabilité.
   */
  verifyAndRecordWidgetPayment = (token) =>
    masterClient
      .mutate({
        mutation: RECORD_WIDGET_PAYMENT,
        variables: {
          token,
          amount: this.data.amount,
          reference: this.data.reference,
        },
      })
      .then(({ data }) => {
        const recorded = data && data.paylineRecordWidgetPayment;
        if (!recorded) throw new Error('paylineRecordWidgetPayment: réponse vide');
        return recorded;
      })
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('[payment] Tournament.recordWidgetPayment.fallback', err.message);
        return checkPaymentWithDelivery(apolloClient, { token });
      });

  /* */
  handlePaylinePaymentSuccess = (eventData) => {
    if (this.isPaylinePaymentHandled || !this.paylineWidgetData) {
      return;
    }

    this.isPaylinePaymentHandled = true;

    const token = eventData?.token || this.paylineWidgetData.token;
    const saveImprint = sessionStorage.getItem('payline_saveImprint') === '1';

    this.paylineWidgetData = null;
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      error: null,
      waiting: true,
      status: 'Vérification du paiement...',
    });

    const processPayment = saveImprint
      ? captureWithTokenWithDelivery(apolloClient, {
          token,
          amount: this.data.amount,
          uuid: this.data.walletUuid,
          reference: this.data.reference,
          customer: {
            firstName: this.props.account.firstname,
            lastName: this.props.account.lastname,
            email: this.props.account.email,
          },
        })
      : this.verifyAndRecordWidgetPayment(token);

    return processPayment
      .then((result) => {
        // `paylineCheckPayment` ne lève jamais : un paiement REFUSÉ revient en résolution
        // normale, avec un `transactionId` quand même (Monext en attribue un aux refus).
        // Sans ce garde, l'inscription était livrée sur un paiement refusé (même trou que
        // l'incident LAMMENS du 21/07 sur le rechargement Practice).
        assertWidgetPaymentSuccess(result, 'Tournament.widgetPayment');

        const transaction = {
          mode: 0, // par CB
          callNo: result.authorizationNumber,
          amount: this.data.amount,
          transactionNo: result.transactionId,
          card: result.maskedCardNumber,
          ...(result.cardBrand && { brand: result.cardBrand }),
          ...(result.cardCountry && { country: result.cardCountry }),
        };

        this.data.transaction = transaction;

        /*
         * Verdict du serveur. Variable LOCALE et non champ d'instance, a dessein :
         * les deux branches qui suivent appellent `asyncSetEntry` chacune de leur
         * cote et sont toutes deux dans cette portee, si bien qu'une locale suffit.
         * Un champ d'instance aurait demande une remise a zero par tentative, dont
         * l'oubli est precisement le defaut releve par la relecture du 20/08 sur
         * `entryConfirmed`.
         *
         * `=== true` et rien d'autre : le champ est nul sur tout serveur anterieur
         * au lot du 21/08, et une lecture laxiste ferait sauter l'inscription
         * devant un serveur qui n'a rien livre.
         */
        const dejaLivre = result?.delivery?.done === true;

        if (saveImprint) {
          this.setState({ status: 'Enregistrement de la carte...' });
          // Carte créée à la volée : on reporte son id dans la transaction pour
          // que setEntry rattache le paiement, sinon credit_card_id reste NULL.
          const newCreditCardId = helpers.uuid.createUuid();
          transaction.creditCardId = newCreditCardId;
          return CreditCard.api
            .setCreditCard(apolloClient, {
              id: newCreditCardId,
              token: result.token || token,
              number: result.maskedCardNumber,
              name: 'Payline',
              dueDate: result.expiryDate ? result.expiryDate : result.expirationDate ? result.expirationDate : '0000',
              brand: result.cardBrand,
              country: result.cardCountry,
              walletId: result.walletId,
              paymentRecordId: result.transactionId,
            })
            .then(() => {
              sessionStorage.removeItem('payline_saveImprint');
              return CreditCard.api
                .getCreditCards(apolloClient, { accountId: this.props.account.id })
                .then(({ creditCards }) => {
                  if (creditCards && creditCards.length > 0) {
                    this.setState({ imprint: creditCards[0] });
                  }
                })
                .catch(() => {});
            })
            .then(() => {
              this.setState({ status: 'Nous confirmons votre paiement...' });

              // Le serveur a deja inscrit : rappeler `setEntry` rejouerait l'action
              // 71 du PGB, qui est IRREVERSIBLE.
              if (dejaLivre) return null;

              const { enter, golf } = this.props;
              return Event.functions.asyncSetEntry(apolloClient, { enter, golf, transaction });
            });
        }

        sessionStorage.removeItem('payline_saveImprint');
        if (dejaLivre) return null;

        const { enter, golf } = this.props;
        return Event.functions.asyncSetEntry(apolloClient, { enter, golf, transaction });
      })
      .then(() => {
        // Par `concludeEntry` et non en direct : c'est le troisieme chemin qui
        // conclut une inscription sur cet ecran, et il doit poser `entryConfirmed`
        // comme les deux autres. Sans quoi la garde de `handlePostCaptureFailure`
        // ne le couvrirait pas.
        this.concludeEntry(this.data.transaction);
      })
      .catch((err) => {
        this.isPaylinePaymentHandled = false;

        /*
         * L'INSCRIPTION EST-ELLE DEJA ACQUISE ? Cette question doit venir en
         * PREMIER, avant toute remise a zero. `concludeEntry` pose
         * `entryConfirmed` PUIS navigue : si la navigation leve, on tombe ici avec
         * une inscription qui tient et un golfeur deja debite. Effacer la
         * transaction et afficher une erreur lui rend alors un formulaire de
         * paiement intact, et un seul appui rejoue le debit.
         *
         * `handlePostCaptureFailure` porte deja exactement cette garde, on la
         * reutilise plutot que de la recopier. Constat de relecture du 24/08/2026 :
         * le defaut preexistait, mais il devient certain sur la branche `dejaLivre`
         * du lot widget, ou l'inscription est un fait etabli par le serveur.
         */
        if (this.entryConfirmed) {
          this.handlePostCaptureFailure(err);
          return;
        }

        /*
         * Ce chemin ne passe pas par `handlePostCaptureFailure`, et il remet la
         * transaction à zéro avant d'afficher : le verdict doit donc être lu ICI,
         * sinon le golfeur qui règle avec une nouvelle carte par le widget, cas le
         * plus fréquent, lirait la chaîne technique brute « errors.payment.reversed »
         * telle quelle. Rien ne la traduit en aval.
         */
        const verdict = [err.message, ...(err.graphQLErrors || []).map((e) => e && e.message)]
          .filter(Boolean)
          .join(' ');

        this.data.transaction = null;
        this.props.enter.transaction = null;
        this.setState({
          error: verdict.includes('errors.payment.reversed')
            ? 'Votre inscription n’a pas pu être enregistrée par le golf et votre paiement a été annulé. '
              + 'Aucun montant ne restera débité : vous pouvez réessayer.'
            : err.message || 'Erreur lors de la vérification du paiement.',
          waiting: false,
          status: null,
        });
      });
  };

  /* */
  handlePaylinePaymentError = (eventData, defaultMessage) => {
    this.paylineWidgetData = null;
    if (this.removePaylineListeners) {
      this.removePaylineListeners();
      this.removePaylineListeners = null;
    }

    this.setState({
      waiting: false,
      status: null,
      error: eventData?.message || defaultMessage,
    });

    this.isPaylinePaymentHandled = false;
  };

  /* */
  render() {
    if (this.redirect) {
      return <Redirect to={`/tournament/${this.id}`} />;
    }

    const { golf } = this.props;
    const { error, status, imprint, loading, waiting, checkEnterEvents } = this.state;
    const { amount } = this.data;

    return (
      <Screen error={error} loading={loading} title='Compétitions - Paiement par CB'>
        {checkEnterEvents ? (
          <>
            <Payment
              amount={amount}
              golf={golf}
              imprint={imprint}
              currency={this.currency}
              session3DS={this.session3DS}
              isPaylineProvider={PaymentProvider.isPayline(this.props.golf)}
              payWithNewCard={this.payWithNewCard}
              payWithNewCardPayline={this.payWithNewCardPayline}
              payWithCardImprint={this.payWithCardImprint}
            />
            {this.state.paylineToken && (
              <PaylineWidgetFrame
                token={this.state.paylineToken}
                options={{ flowType: PAYLINE_FLOW_TYPES.CHECKOUT, amount: amount }}
                title='Payline Widget'
              />
            )}
          </>
        ) : (
          <Typography color='error' align='center' variant='h6'>
            {i18n.getFromOpts('event_pay.not_complete1', golf.options)}
            <br />
            {i18n.getFromOpts('event_pay.not_complete2', golf.options)}
          </Typography>
        )}
        <DialogPaymentIP open={waiting} status={status} />
      </Screen>
    );
  }
}

const mapStateToProps = ({ app, eventEnter }) => ({
  golf: app.golf,
  enter: eventEnter,
  account: app.account,
});

const StyledComponent = withStyles(styles)(TournamentPayment);

export default connect(mapStateToProps, Event.actions)(StyledComponent);
